/**
 * GridViz — visualises a 2D array as a color-coded grid.
 *
 * Accepts either a GridState (extracts the named layer) or a raw number[][].
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@/graph/Payload';
import { params, internals } from '@/nodes/TypedEndpoints';
import type { GridState } from '../GridState';
import { logger } from '@/logging';

const endpoints = {
  ...params({
    layer: { dataType: 'string', value: '', info: 'Layer name to display (when input is GridState)' },
    mode: { value: 'color', options: ['color', 'heatmap', 'terrain', 'ascii'] as const, info: 'Display mode' },
  }),
  ...internals({
    in:  { dataType: 'any' as any, isInput: true, label: 'In' },
    out: { dataType: 'array', isOutput: true, label: 'Out', pushToStore: true },
  }),
};

class GridVizNode extends BaseNode<typeof endpoints> {
  onParamChanged(_payload: Payload, endpointId: string): void {
    if (endpointId === 'layer') {
      // Re-extract from stored input
      this.extractAndPush();
    }
  }

  onInternalChanged(payload: Payload, endpointId: string): void {
    if (endpointId === 'in') {
      const val = (payload as any)?.value ?? payload;
      logger.trace('GridViz', `received on 'in': type=${typeof val}, isArray=${Array.isArray(val)}, hasLayers=${'layers' in (val ?? {})}`);
      this.lastInput = val;
      this.extractAndPush();
    }
  }

  private lastInput: any = null;

  private extractAndPush(): void {
    const val = this.lastInput;
    if (!val) return;

    logger.trace('GridViz', `extractAndPush: val type=${typeof val}, layerParam='${this.params.layer.value}'`);
    // Check if it's a GridState
    if (val && typeof val === 'object' && 'layers' in val && 'cols' in val) {
      const state = val as GridState;
      const layerName = this.params.layer.value;
      if (layerName && state.layers[layerName]) {
        this.internals.out.set(state.layers[layerName]);
      } else {
        // Use first layer if no name specified
        const names = Object.keys(state.layers);
        if (names.length > 0) {
          this.internals.out.set(state.layers[names[0]]);
        }
      }
    } else if (Array.isArray(val)) {
      // Raw number[][] pass-through
      this.internals.out.set(val);
    }
  }
}

export const gridVizNodeDef: NodeDef<GridVizNode> = {
  type: 'GridViz',
  shortName: 'gridviz',
  label: 'Grid Viz',
  info: 'Visualises a GridState layer or raw 2D array as a color-coded grid.',
  domain: Domain.Data,
  endpoints,
  ctor: GridVizNode,
};
